import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers, createUser, updateUser, deleteUser, getRoles } from '../../redux/actions/UserManagement/userManagement';
import TableComponent from '../../common/components/TableComponent/TableComponent';
import Actions from 'common/components/Actions/Actions';
import DeleteModal from 'common/components/Modal/DeleteModal';
import Toast from 'common/components/Toast/Toast';
import { MotifIcon, MotifProgressLoader } from '@ey-xd/motif-react';
import { actionIcDelete24px} from "@ey-xd/motif-react/assets/icons";
import AddUser from './AddUser';
import StatusBadge from 'common/components/StatusBadge/StatusBadge';

const columns = [
  { field: 'userName', headerName: 'User Name', sortable: true, width: 300 },
  { field: 'email', headerName: 'User Email', sortable: true, width: 300 },
  { field: 'role', headerName: 'User Role', sortable: true, width: 200 },
  { cellRenderer: 'badgeCellRenderer', field: 'status', headerName: 'User Status', sortable: true, width: 200 },
  {
    cellRenderer: 'actionsCellRenderer',
    field: 'actions',
    pinned: 'right',
    headerComponentParams: {
      enableSorting: false,
      showFilter: false
    },
    headerName: '',
    width: 250
  }
];

function UserManagement() {
  const dispatch = useDispatch();
  const data = useSelector((state: any) => state?.userManagement?.data) || [];
  const loading = useSelector((state: any) => state?.userManagement?.loading) || false;
  const currentTenantId = useSelector((state: any) => state?.user?.currentTenant?.TenantId);
  const loggedInEmail = (sessionStorage.getItem('user_mail') || '').toLowerCase();
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<string>('');
  const [deleteUserName, setDeleteUserName] = useState<string>('');
  const [deleteUserEmail, setDeleteUserEmail] = useState<string>('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastData, setToastData] = useState<any>();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [form, setForm] = useState<any>({ UserFullName: '', UserEmail: '', Role: '', role_id: '', IsActive: true });

  useEffect(() => {
    dispatch(getUsers());
  }, []);

  useEffect(() => {
    if (currentTenantId) {
      dispatch(getUsers());
    }
  }, [currentTenantId]);

  const openAdd = () => {
    dispatch(getRoles());
    setEditItem(null);
    setForm({ UserFullName: '', UserEmail: '', Role: '', role_id: '', IsActive: true });
    setShowForm(true);
  };

  const openEdit = (row: any) => {
    dispatch(getRoles());
    const raw = row?._raw || {};
    setEditItem(raw);
    setForm({
      UserFullName: raw.UserFullName || row.userName || '',
      UserEmail: raw.UserEmail || row.email || '',
      Role: raw.Role || row.role || '',
      role_id: raw.role_id || raw.RoleId || '',
      IsActive: typeof raw.IsActive === 'boolean' ? raw.IsActive : row.status === 'Active',
      UserId: raw.UserId,
    });
    setShowForm(true);
  };

  const handleSave = () => {
    const payload = editItem
      ? {
          IsActive: form.IsActive,
          Role: form.Role,
          role_id: form.role_id,
          UserId: form.UserId,
        }
      : form;
    const action = editItem ? updateUser(payload) : createUser(payload);
    dispatch(action)
      .then(() => dispatch(getUsers()))
      .then(() => {
        setShowForm(false);
        setToastVisible(true);
        setToastData({
          header: 'Success',
          message: editItem ? 'User updated successfully.' : 'User created successfully.',
          variant: 'success',
        });
      })
      .catch((e:any) => {
        setToastVisible(true);
        setToastData({ header: 'Failed', message: e?.response?.data?.message || 'Operation failed. Please try again.', variant: 'error' });
      });
  };

  const actionHandler = (row: any, actionType: 'edit' | 'delete') => {
    if (actionType === 'edit') return openEdit(row);
    if (actionType === 'delete') {
      const raw = row?._raw || {};
      setDeleteUserId(raw.UserId);
      setDeleteUserEmail(raw.UserEmail);
      setDeleteUserName(raw.UserFullName || row.userName || '');
      setIsDeleteVisible(true);
    }
  };

  const handleConfirmDelete = () => {
    dispatch(deleteUser(deleteUserId, deleteUserEmail))
      .then(() => {
        setIsDeleteVisible(false);
        setToastVisible(true);
        setToastData({ header: 'Success',message: 'User deleted Successfully.', variant: 'success' });
      })
      .catch(() => {
        setToastVisible(true);
        setToastData({ header: 'Failed', message: 'Failed to delete user. Please try again.', variant: 'error' });
      });
  };

  return (
    <>
      <MotifProgressLoader show={loading} className="fullscreen-progress-loader" />
      {showForm ? (
        <AddUser
          form={form}
          setForm={setForm}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
          editItem={editItem}
        />
      ) : (
      <TableComponent
        breadCrumbs={<>Settings {'>'} User Management</>}
        title="User Management"
        description="Manage user accounts, roles, and permissions across the application."
        columns={columns}
        columnActions={{
          actionsCellRenderer: (p: any) => (
            <Actions
              items={[
                {
                  label: 'Edit',
                  onClick: () => actionHandler(p?.data, 'edit'),
                  variant: 'secondary',
                  size: 'medium',
                },
                {
                  label: <MotifIcon src={actionIcDelete24px} style={{ width: 20 }} />,
                  onClick: () => {
                    const rowEmail = (p?.data?.email || '').toLowerCase();
                    if (rowEmail === loggedInEmail) return; // prevent self-delete
                    actionHandler(p?.data, 'delete');
                  },
                  disabled: ((p?.data?.email || '').toLowerCase() === loggedInEmail),
                  variant: 'secondary',
                  size: 'small',
                },
              ]}
            />
          ),
          badgeCellRenderer: (p: any) => (
            <StatusBadge status={p.value || ''} />
          ),
        }}
        tabCount={data.length}
        data={data}
        loading={loading}
        searchPlaceholder="Search"
        searchKeys={['userName', 'email']}
        actionButtonLabel="+ Add New User"
        onActionButtonClick={openAdd}
        emptyStateMessage='No users found'
      />
      )}

      <DeleteModal
        isVisible={isDeleteVisible}
        setIsVisible={setIsDeleteVisible}
        message={<span><br/> Are you sure you want to delete this User? <br /> This action cannot be undone.</span>}
        columnData={'User <' + deleteUserName + '>'}
        onConfirmClick={handleConfirmDelete}
      />
      <Toast
        visible={toastVisible}
        setVisible={setToastVisible}
        children={
          <div>
            <div className={'toastHeader'}>{toastData?.header}</div>
            <div className={'toastContent'}>{toastData?.message}</div>
          </div>
        }
        variant={toastData?.variant}
      />
    </>
  );
}

export default UserManagement;